// Temu Affiliate Import Page
// Admin can add Temu products via CJ Affiliate/ShareASale links
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, Link2, ExternalLink, RefreshCw, Trash2, Edit3, 
  BarChart3, MousePointer2, ArrowLeft, Save, X, ChevronRight 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TemuImportPage = () => {
  const { getAuthHeader } = useAuth();
  
  // Products state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'General',
    image_url: '',
    temu_link: '',
    affiliate_link: '',
    affiliate_network: 'cj',
    commission_rate: '',
    featured: false,
    tags: []
  });
  const [saving, setSaving] = useState(false);

  // Fetch products
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/temu/products`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error('Failed to load Temu products');
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/temu/analytics`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchAnalytics();
  }, []);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'General',
      image_url: '',
      temu_link: '',
      affiliate_link: '',
      affiliate_network: 'cj',
      commission_rate: '',
      featured: false,
      tags: []
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        commission_rate: formData.commission_rate ? parseFloat(formData.commission_rate) : null,
        tags: formData.tags.length ? formData.tags : []
      };
      
      if (editingProduct) {
        await axios.put(`${API_URL}/api/temu/products/${editingProduct.id}`, payload, {
          headers: getAuthHeader(),
          withCredentials: true
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/api/temu/products`, payload, {
          headers: getAuthHeader(),
          withCredentials: true
        });
        toast.success('Temu product added');
      }
      
      resetForm();
      fetchProducts();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  // Edit product
  const startEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      temu_link: product.temu_data?.original_link || '',
      affiliate_link: product.affiliate_url,
      affiliate_network: product.temu_data?.affiliate_network || 'cj',
      commission_rate: product.commission_rate?.toString() || '',
      featured: product.featured,
      tags: product.tags?.filter(t => t !== 'temu' && t !== 'affiliate') || []
    });
    setEditingProduct(product);
    setShowForm(true);
  };

  // Delete product
  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this Temu product?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/temu/products/${productId}`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      toast.success('Product deleted');
      fetchProducts();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
            <Link to="/admin" className="hover:text-white transition-colors">Admin</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Temu Affiliate</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Temu Affiliate Products</h1>
              <p className="text-slate-400 text-sm mt-1">
                Add Temu products via CJ Affiliate or ShareASale links
              </p>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="add-temu-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Temu Product
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.temu_products}</p>
                    <p className="text-sm text-slate-400">Temu Products</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <MousePointer2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.total_clicks}</p>
                    <p className="text-sm text-slate-400">Total Clicks (30d)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.by_network?.cj || 0}</p>
                    <p className="text-sm text-slate-400">CJ Clicks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.by_network?.shareasale || 0}</p>
                    <p className="text-sm text-slate-400">ShareASale Clicks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="bg-slate-900 border-slate-800 w-full max-w-2xl max-h-[90vh] overflow-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">
                    {editingProduct ? 'Edit Temu Product' : 'Add Temu Product'}
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Add affiliate products from Temu via CJ or ShareASale
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={resetForm}>
                  <X className="w-5 h-5 text-slate-400" />
                </Button>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Product Name */}
                  <div>
                    <label className="text-sm font-medium text-slate-300 block mb-2">Product Name</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Wireless Earbuds Pro"
                      required
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="temu-name-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Temu branding will be automatically removed</p>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="text-sm font-medium text-slate-300 block mb-2">Description</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Product description..."
                      required
                      rows={3}
                      className="bg-slate-800 border-slate-700 text-white resize-none"
                    />
                  </div>
                  
                  {/* Price & Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-300 block mb-2">Price (USD)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="19.99"
                        required
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300 block mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
                      >
                        <option value="Electronics">Electronics</option>
                        <option value="Fashion">Fashion</option>
                        <option value="Home">Home & Garden</option>
                        <option value="Sports">Sports & Outdoors</option>
                        <option value="Beauty">Beauty & Health</option>
                        <option value="General">General</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Image URL */}
                  <div>
                    <label className="text-sm font-medium text-slate-300 block mb-2">Image URL</label>
                    <Input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="https://..."
                      required
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  
                  <Separator className="bg-slate-700" />
                  
                  {/* Affiliate Links */}
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-orange-400">Affiliate Configuration</h4>
                    
                    {/* Original Temu Link */}
                    <div>
                      <label className="text-sm font-medium text-slate-300 block mb-2">
                        Original Temu Product Link
                      </label>
                      <Input
                        type="url"
                        value={formData.temu_link}
                        onChange={(e) => setFormData({...formData, temu_link: e.target.value})}
                        placeholder="https://www.temu.com/product-..."
                        required
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="temu-link-input"
                      />
                    </div>
                    
                    {/* Affiliate Network */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-300 block mb-2">
                          Affiliate Network
                        </label>
                        <select
                          value={formData.affiliate_network}
                          onChange={(e) => setFormData({...formData, affiliate_network: e.target.value})}
                          className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
                        >
                          <option value="cj">CJ Affiliate</option>
                          <option value="shareasale">ShareASale</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-300 block mb-2">
                          Commission Rate (%)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.commission_rate}
                          onChange={(e) => setFormData({...formData, commission_rate: e.target.value})}
                          placeholder="e.g., 10"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                    
                    {/* Affiliate Link */}
                    <div>
                      <label className="text-sm font-medium text-slate-300 block mb-2">
                        Your Affiliate Link (from CJ/ShareASale)
                      </label>
                      <Input
                        type="url"
                        value={formData.affiliate_link}
                        onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
                        placeholder="https://www.cjdropship.com/..."
                        required
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="affiliate-link-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        This is the tracking link customers will be redirected to
                      </p>
                    </div>
                  </div>
                  
                  {/* Featured Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Featured Product</label>
                      <p className="text-xs text-slate-500">Show on homepage</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, featured: !formData.featured})}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        formData.featured ? 'bg-emerald-500' : 'bg-slate-700'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                        formData.featured ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  
                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                      className="flex-1 border-slate-700 text-slate-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      data-testid="save-temu-product-btn"
                    >
                      {saving ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Products List */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Your Temu Products</CardTitle>
            <CardDescription className="text-slate-400">
              Manage your Temu affiliate product catalog
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 mx-auto text-slate-500 animate-spin mb-3" />
                <p className="text-slate-500">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Link2 className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <h3 className="text-white font-medium mb-2">No Temu Products Yet</h3>
                <p className="text-slate-500 mb-4">
                  Add your first Temu affiliate product to start earning commissions
                </p>
                <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Product
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <img
                      src={product.image_url || 'https://via.placeholder.com/80'}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{product.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-emerald-400 font-medium">${product.price}</span>
                        <Badge variant="outline" className="text-xs border-orange-500/30 text-orange-400">
                          {product.temu_data?.affiliate_network?.toUpperCase() || 'Affiliate'}
                        </Badge>
                        {product.commission_rate && (
                          <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                            {product.commission_rate}% commission
                          </Badge>
                        )}
                      </div>
                      {product.click_count > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {product.click_count} clicks
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(product.affiliate_url, '_blank')}
                        className="text-slate-400 hover:text-white"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEdit(product)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteProduct(product.id)}
                        className="text-slate-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Help Section */}
        <Card className="bg-slate-900/50 border-slate-800 mt-6">
          <CardContent className="pt-6">
            <h4 className="text-white font-medium mb-3">How to Add Temu Affiliate Products</h4>
            <ol className="text-slate-400 text-sm space-y-2 list-decimal list-inside">
              <li>Find a product on Temu you want to promote</li>
              <li>Get the affiliate link from CJ Affiliate or ShareASale</li>
              <li>Copy the product image URL from Temu</li>
              <li>Add the product here with your affiliate tracking link</li>
              <li>Customers click &rarr; tracked &rarr; you earn commission!</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TemuImportPage;
